/* eslint-disable no-console */
import "./dashboard-table.css";
import { Table, Input, Space, Tooltip } from "antd";
import type { TableProps } from "antd";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Heading from "../../../components/heading";
import { useNavigate } from "react-router-dom";
import {
  getStatusInfo,
  getUserRole,
  isSupplierRole,
  isExternalSupplierRole,
  wrappedText,
  disableViewDetails,
  formatToLongDate,
  disableDownloadDTSBtn
} from "../../../components/Shared/utils";
import { useOpenNotification } from "../../../components/Notification/notification";
import { syncAdobeSign, syncAdobeSignWithRetry } from "../../../services/sync-all-adobe-sign";
import { ExportOutlined, DownloadOutlined } from "@ant-design/icons";
import { downloadFileUsingPreSignedURL } from "../../PackageHistory/utils";
import { downloadSignedDTS } from "../../../services/download-signed-dts";
import { isEqual } from 'lodash';
import { getUserEmail } from "../../../services/utils";
import OfflineDtsPopup from "./offline-dts-dailog";
import SupplierOrgDialog from "./supplier-org-dialog";

interface ActionItem {
  text: string;
  color?: string;
}

export interface DataType {
  status?: string;
  key: string;
  record_id?: string;
  compound: string;
  protocol_number: string;
  dts_version: string;
  transfer_id: string;
  last_action: any;
  supplier_org: any;
  pending_action: any;
  modified?: string;
  modified_date: string;
  updated_when?: string;
  action: ActionItem[];
  pending_action_tag?: ActionItem[];
  modified_date_raw: number;
}

interface DashboardProps {
  accessRole?: string; // Optional prop to pass the user role
}

type StatusKey =
  | "saveDraft"
  | "submitted"
  | "approveAgreement"
  | "finalizeAgreement"
  | "supplierUpload"
  | "finalizeTransfer"
  | "Cancelled";

const { Search } = Input;

const DashboardTable: React.FC<DashboardProps> = ({
  accessRole,
}) => {
  const [dataList, setDataList] = useState<DataType[]>([]);
  const [originalDataList, setOriginalDataList] = useState<DataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { openNotification, openNotificationWithAlert, contextHolder } = useOpenNotification();
  const [compoundFilters, setCompoundFilters] = useState<
    { text: string; value: string }[]
  >([]);
  const [protocolNumberFilters, setProtocolNumberFilters] = useState<
    { text: string; value: string }[]
  >([]);
  const [dtsVersionFilters, setDtsVersionFilters] = useState<
    { text: string; value: string }[]
  >([]);
  const [transferIdFilters, setTransferIdFilters] = useState<
    { text: string; value: string }[]
  >([]);
  const [supplierFilters, setSupplierFilters] = useState<
    { text: string; value: string }[]
  >([]);
  const [lastActionFilters, setLastActionFilters] = useState<
    { text: string; value: string }[]
  >([]);
  const [modifiedDateFilters, setModifiedDateFilters] = useState<
    { text: string; value: string }[]
  >([]);
  const [pendingActionFilters, setPendingActionFilters] = useState<
    { text: string; value: string }[]
  >([]);
  const [failedList, setFailedList] = useState<any[]>([]);
  const [isOfflineDtsModalOpen, setIsOfflineDtsModalOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const [showFirstLoginPopup, setShowFirstLoginPopup] = useState<boolean>(() => {
    if (!isExternalSupplierRole()) return false;
    const profileRaw = sessionStorage.getItem("profile");
    const profile = profileRaw ? JSON.parse(profileRaw) : null;
    return !profile?.organisation;
  });
  const navigate = useNavigate(); // Initialize useNavigate
  const userRole = getUserRole();
  const isSupplierUser = isSupplierRole();
  const isAdminUser = userRole?.toLowerCase() === "admin";

  // State to track loading status
  const [downloadRowKey, setDownloadRowKey] = useState<string | null>(null); // State to track which row is downloading
  // Function to extract unique filters
  const extractUniqueFilters = (data: DataType[], key: keyof DataType) => {
    return Array.from(new Set(data.map((item) => item[key])))
      .sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          return dateA.getTime() - dateB.getTime(); // Sort by date if both are valid dates
        }
        return String(a).localeCompare(String(b)); // Default string comparison
      })
      .map((value) => ({
        text: value,
        value,
      }));
  };

  const handleExportClick = (rowData: DataType) => {
    // Find the row data based on the record_id
    const selectedRowData = originalDataList.find(
      (item) => item.record_id === rowData.key,
    );
    // Navigate to DataTransfer component and pass the selected row data
    if (selectedRowData) {
      const updatedRowData: DataType = {
        ...selectedRowData,
        key: selectedRowData.record_id || rowData.key,
        status: rowData.status
      };
      navigate("/data-transfer", { state: { rowData: updatedRowData } });
    }
  };

  const handleDTSDownloadClick = async (rowData: DataType) => {
    setDownloadRowKey(rowData.key);
    const data: any = originalDataList.find(item => item.record_id === rowData.key);

    if (data) {
      try {
        const downloadUrl = await downloadSignedDTS(
          data.msd.split('-')[0],
          data.protocol_number,
          data.dts_version,
          data.transfer_id.split('-')[0],
          data.supplier_org,
          formatToLongDate(data.created_when),
          true,
        );
        downloadFileUsingPreSignedURL(downloadUrl);
      } catch (error) {
        openNotification("error", "Failed to download DTS");
      } finally {
        setDownloadRowKey(null);
      }
    }
  };

  const allColumns: TableProps<DataType>["columns"] = [
    {
      title: "",
      dataIndex: "refresh",
      key: "refresh",
      width: "11%",
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tooltip
            title={""}
            key={`export-${record.key}`}>
            <span
              id={
                disableViewDetails(record.pending_action, isSupplierUser)
                  ? "disabled-icon"
                  : "edit-icon"
              }
              onClick={() =>
                !disableViewDetails(record.pending_action, isSupplierUser) &&
                handleExportClick(record)
              }
              style={{
                cursor: disableViewDetails(record.pending_action, isSupplierUser) ? "default" : "pointer",
                color: disableViewDetails(record.pending_action, isSupplierUser) ? "#bfbfbf" : "black",
              }}
            >
              <ExportOutlined />&nbsp;View Details
            </span>
          </Tooltip>
          <Tooltip
            title={disableDownloadDTSBtn(record.pending_action, downloadRowKey === record.key) ? "" : "Download DTS"}
            key={`Download-DTS-${record.key}`}
          >
            <span
              id={
                disableDownloadDTSBtn(record.pending_action, downloadRowKey === record.key)
                  ? "disabled-icon"
                  : "download-icon"
              }
              onClick={() =>
                !disableDownloadDTSBtn(record.pending_action, downloadRowKey === record.key) &&
                handleDTSDownloadClick(record)
              }
              style={{ cursor: disableDownloadDTSBtn(record.pending_action, downloadRowKey === record.key) ? "default" : "pointer" }}
            >
              <DownloadOutlined />
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Compound",
      dataIndex: "compound",
      key: "compound",
      width: "10%",
      filters: compoundFilters,
      onFilter: (value: any, record: DataType) => record.compound === value, // Adjusted type
      sorter: (a, b) => a.compound.localeCompare(b.compound),
    },
    {
      title: "Protocol",
      dataIndex: "protocol_number",
      key: "protocol_number",
      width: "7%",
      filters: protocolNumberFilters,
      onFilter: (value: any, record: DataType) =>
        record.protocol_number === value, // Adjusted type
      sorter: (a, b) => a.protocol_number.localeCompare(b.protocol_number),
    },
    {
      title: "DTS version",
      dataIndex: "dts_version",
      key: "dts_version",
      width: "9%",
      filters: dtsVersionFilters,
      onFilter: (value: any, record: DataType) => record.dts_version === value, // Adjusted type
      sorter: (a, b) => parseFloat(a.dts_version) - parseFloat(b.dts_version),
    },
    {
      title: "Transfer ID",
      dataIndex: "transfer_id",
      key: "transfer_id",
      width: "15%",
      render: (text: any) =>
        text !== null && text !== "" ? (
          <Tooltip title={text} placement="right">
            {wrappedText(text, 40)}
          </Tooltip>
        ) : (
          "-"
        ),
      filters: transferIdFilters,
      onFilter: (value: any, record: DataType) => record.transfer_id === value, // Adjusted type
      sorter: (a, b) => a.transfer_id.localeCompare(b.transfer_id),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_org",
      key: "supplier",
      width: "10%",
      filters: supplierFilters,
      onFilter: (value: any, record: DataType) => record.supplier_org === value,
      sorter: (a, b) => a.supplier_org.localeCompare(b.supplier_org),
    },
    {
      title: "Last Action Completed",
      dataIndex: "last_action",
      key: "last_action",
      width: "10%",
      filters: lastActionFilters,
      onFilter: (value: any, record: DataType) => record.last_action === value,
      sorter: (a, b) => a.last_action.localeCompare(b.last_action),
      render: (text: any) =>
        text !== null && text !== "" ? (
          <Tooltip title={text} placement="right">
            {wrappedText(text, 25)}
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Last Action Completed Date",
      dataIndex: "modified_date",
      key: "modified_date",
      width: "10%",
      defaultSortOrder: "descend",
      filters: modifiedDateFilters,
      onFilter: (value: any, record: DataType) =>
        record.modified_date === value,
      sorter: (a, b) => a.modified_date_raw - b.modified_date_raw,
    },
    {
      title: "Current Pending Action",
      dataIndex: "pending_action",
      key: "pending_action",
      width: "10%",
      filters: pendingActionFilters,
      onFilter: (value: any, record: DataType) =>
        record.pending_action === value,
      sorter: (a, b) => a.pending_action.localeCompare(b.pending_action),
    },
  ];

  // Filter out the "Supplier" column if userRole is "supplier"
  const columns =
    userRole === "Supplier" || userRole === "Merck_supplier"
      ? allColumns.filter((column) => column.key !== "supplier")
      : allColumns;

  const filteredDataList = useMemo(() => {
    return dataList.filter((item) => {
      return Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchTerm.toLowerCase()),
      );
    });
  }, [dataList, searchTerm]);

  const handleNavigate = () => {
    navigate("/data-transfer"); // Replace with your actual route
  };

  const formatAndSetDataToState = (data: any[]) => {
    const formattedData = data
      .map(
        (item: any, index: number) => {
          const dateStr = item?.updated_when ? item.updated_when : item.created_when;
          const formattedDate =
            dateStr !== "N/A" ? formatToLongDate(dateStr) : "N/A"; // Use custom date formatting
          const rawDate =
            dateStr !== "N/A" ? new Date(dateStr).getTime() : 0;
          const dtsVersion = item?.dts_version
            ? parseFloat(item.dts_version).toFixed(1)
            : "N/A";
          const status = (item?.status as StatusKey) ?? "N/A"; // Type assertion here
          const { lastCompletedAction, currentPendingAction } =
            getStatusInfo(status, item?.is_offline_flow); // Use the function to get status info
          const splitValue = item?.msd.split("-");
          const displayMSDValue = splitValue[0]; // "MK0002"

          return {
            key: item?.record_id,
            compound: displayMSDValue ?? "N/A",
            protocol_number: item?.protocol_number ?? "000",
            dts_version: dtsVersion,
            transfer_id: item?.transfer_id ?? "N/A",
            supplier_org: item?.supplier_org ?? "N/A",
            last_action: lastCompletedAction,
            modified_date: formattedDate,
            modified_date_raw: rawDate,
            status,
            pending_action: currentPendingAction,
            action: [{ text: currentPendingAction }],
          };
        },
      );
    setDataList(formattedData);
    setLoading(false);

    // Set unique filters using the reusable function
    setDtsVersionFilters(
      extractUniqueFilters(formattedData, "dts_version"),
    );
    setTransferIdFilters(
      extractUniqueFilters(formattedData, "transfer_id"),
    );
    setSupplierFilters(
      extractUniqueFilters(formattedData, "supplier_org"),
    );
    setLastActionFilters(
      extractUniqueFilters(formattedData, "last_action"),
    );
    setCompoundFilters(extractUniqueFilters(formattedData, "compound"));
    setProtocolNumberFilters(
      extractUniqueFilters(formattedData, "protocol_number"),
    );
    setModifiedDateFilters(
      extractUniqueFilters(formattedData, "modified_date"),
    );
    setPendingActionFilters(
      extractUniqueFilters(formattedData, "pending_action"),
    );
  };

  const syncHelper = async (listToSync: any[]) => {
    const filteredItems = listToSync.filter(item => (getStatusInfo(item.status).lastCompletedAction === "Draft Saved" || getStatusInfo(item.status).lastCompletedAction === "MSD Created DTS Agreement" || getStatusInfo(item.status).lastCompletedAction === "Supplier Signed DTS Agreement"));

    const filterItemsRecordIds = filteredItems.map(item => item.record_id);

    const { results: syncedData, hasRateLimitError, failedRequests: failedWithRetryList } = await syncAdobeSign(filterItemsRecordIds);

    let resultantList = listToSync;

    if (syncedData.length !== 0) {
      const mappedList = new Map(listToSync.map(item => [item.record_id, item]));

      syncedData.forEach((item: any) => {
        const originalItem = mappedList.get(item.value.data.record_id);
        // const updatedStatus = 'Finalize Agreement';
        // const dateStr = '2026-05-17 09:11:28.019530';
        const updatedStatus = item?.value?.data?.status;
        const dateStr = (item.value?.data?.updated_when)?.replace("T", " ") ?? "N/A";

        if (originalItem) {
          mappedList.set(item.value.data.record_id, {
            ...originalItem,
            status: updatedStatus,
            updated_when: dateStr
          });
        }
      });

      resultantList = Array.from(mappedList.values());
    }

    return { resultantList, hasRateLimitError, failedWithRetryList };
  };

  const initialAdobeSync = async (listToSync: any[]) => {
    const { resultantList, hasRateLimitError, failedWithRetryList } = await syncHelper(listToSync);

    formatAndSetDataToState(resultantList);
    setOriginalDataList(resultantList);

    if (hasRateLimitError) {
      setFailedList(failedWithRetryList);
    }
  };

  useEffect(() => {
    const retryFailedSync = async () => {
      const failedItemsRecordIds = failedList.map(request => ((request as PromiseRejectedResult).reason?.config?.params?.record_id));

      const { results, hasRateLimitError } = await syncAdobeSignWithRetry(failedItemsRecordIds);

      if (hasRateLimitError) {
        openNotification(
          "error",
          "Failed to sync some Adobe sign status. Please try again later.");
        return;
      }

      let resultantList = originalDataList;

      if (results.length !== 0) {
        const mappedList = new Map(originalDataList.map(item => [item.record_id, item]));

        results.forEach((item: any) => {
          const originalItem = mappedList.get(item.value.data.record_id);
          // const updatedStatus = 'Finalize Agreement';
          // const dateStr = '2026-05-25 09:11:28.019530';
          const updatedStatus = item?.value?.data?.status;
          const dateStr = (item.value?.data?.updated_when)?.replace("T", " ") ?? "N/A";

          if (originalItem) {
            mappedList.set(item.value.data.record_id, {
              ...originalItem,
              status: updatedStatus,
              updated_when: dateStr
            });
          }
        });

        resultantList = Array.from(mappedList.values());
      }

      if (!isEqual(originalDataList, resultantList)) {
        openNotificationWithAlert(
          "info",
          "Adobe Sign status has been refreshed. Do you want to refresh the page to see the updated status?",
          "Refresh Page",
          () => {
            formatAndSetDataToState(resultantList);
            setOriginalDataList(resultantList);
          }
        );
        return;
      }

      return;
    };

    if (failedList.length > 0) {
      retryFailedSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedList]);

  useEffect(() => {
    // Access the profile object
    const fetchData = async () => {
      setLoading(true);
      try {
        const apiUrl = `${process.env.REACT_APP_PROXY_URL}/v1/fetchAllRecords`;
        const response = await axios.post(apiUrl, null, {
          headers: {
            Authorization: "Basic " + process.env.REACT_APP_AUTHORIZATION_KEY,
            "x-merck-apikey": process.env.REACT_APP_X_MERCK_API_KEY,
          },
          params: {
            email: getUserEmail() ?? '',
            role: userRole ?? '',
          },
        });

        if (response?.data) {
          const fetchedList = response.data
            .filter((item: any) =>
              !(item?.status === "Cancelled" &&
                item?.reviewer_description === "Transfer cancelled from Save Draft status"));

          setOriginalDataList(fetchedList);
          initialAdobeSync(fetchedList);
        }
      } catch (err) {
        setLoading(false);
        openNotification("error", "failed to load transfer records.");
        return;
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, dashboardRefreshKey]);

  return (
    <>
      <Heading
        title="Dashboard"
        buttons={
          isAdminUser
            ? [
              {
                key: "export",
                label: "Create Offline Agreement",
                onClick: () => setIsOfflineDtsModalOpen(true),
                type: "default",
                id: "create-offline-agreement-btn",
              },
            ]
            : []
        }
        buttonName={
          userRole && userRole.toLowerCase() !== "supplier" && userRole.toLowerCase() !== "merck_supplier"
            ? "Create New Agreement"
            : ""
        }
        onClickNavigate={handleNavigate}
      />
      <div className="dashboard-wrapper" id="dashboard-wrapper-title">
        <div className="table-title">My Tasks</div>
        <div className="dashboard-container">
          <Space direction="vertical">
            <Search
              placeholder="search"
              allowClear
              onSearch={setSearchTerm}
              style={{ width: 463 }}
            />
          </Space>
          <Table<DataType>
            columns={columns}
            dataSource={filteredDataList} // Use filtered data
            tableLayout="fixed"
            size="small"
            pagination={{ pageSize: 6, showSizeChanger: false }}
            loading={loading}
          />
        </div>
      </div>
      <OfflineDtsPopup
        isModalOpen={isOfflineDtsModalOpen}
        setIsOfflineDtsModalOpen={setIsOfflineDtsModalOpen}
        openNotification={openNotification}
        onCreateSuccess={() => setDashboardRefreshKey((prev) => prev + 1)}
      />
      {showFirstLoginPopup && (
        <SupplierOrgDialog
          onComplete={() => setShowFirstLoginPopup(false)}
        />
      )}
      {contextHolder}
    </>
  );
};

export default DashboardTable;
